import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { History, Gem, RotateCw } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import type { WheelSpinLog } from '@/hooks/useAdminWheelData';

interface WheelSpinHistoryProps {
  spins: WheelSpinLog[];
}

const getPrizeDescription = (spin: WheelSpinLog): string => {
  switch (spin.prizeType) {
    case 'nothing':
      return 'perdeu';
    case 'gems':
      return `ganhou ${spin.prizeValue} gema${spin.prizeValue > 1 ? 's' : ''}`;
    case 'boost':
      return `ganhou ${spin.prizeValue} boost${spin.prizeValue > 1 ? 's' : ''}`;
    case 'vip_day':
      return `ganhou ${spin.prizeValue} dia${spin.prizeValue > 1 ? 's' : ''} VIP`;
    default:
      return 'jogou';
  }
};

const getPrizeColor = (prizeType: string): string => {
  switch (prizeType) {
    case 'nothing':
      return 'text-gray-400';
    case 'gems':
      return 'text-purple-400';
    case 'boost':
      return 'text-yellow-400';
    case 'vip_day':
      return 'text-luxury-gold';
    default:
      return 'text-white';
  }
};

const WheelSpinHistory: React.FC<WheelSpinHistoryProps> = ({ spins }) => {
  return (
    <Card className="bg-white/5 border-white/10 backdrop-blur-sm">
      <CardHeader>
        <CardTitle className="text-white flex items-center gap-2">
          <History className="w-5 h-5 text-blue-400" />
          Histórico de Giros
        </CardTitle>
      </CardHeader>
      <CardContent>
        {spins.length === 0 ? (
          <div className="text-center py-8">
            <div className="w-16 h-16 bg-gray-800/50 rounded-full flex items-center justify-center mx-auto mb-4">
              <RotateCw className="w-8 h-8 text-gray-400" />
            </div>
            <p className="text-muted-foreground text-sm">
              Nenhum giro registrado ainda
            </p>
          </div>
        ) : (
          <ScrollArea className="h-[400px] pr-4">
            <div className="space-y-2">
              {spins.map((spin) => (
                <div 
                  key={spin.id}
                  className="flex items-center gap-3 p-3 bg-black/30 rounded-lg border border-white/5 hover:border-white/10 transition-colors"
                >
                  <span className="text-xl flex-shrink-0">{spin.prizeEmoji}</span>
                  
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-white truncate">
                      <span className="font-medium">{spin.username}</span>{' '}
                      <span className={getPrizeColor(spin.prizeType)}>
                        {getPrizeDescription(spin)}
                      </span>
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {formatDistanceToNow(new Date(spin.createdAt), {
                        addSuffix: true,
                        locale: ptBR,
                      })}
                    </p>
                  </div>

                  <Badge 
                    variant="outline"
                    className={`flex-shrink-0 ${
                      spin.wasFree 
                        ? 'border-green-500/30 text-green-300 bg-green-900/20' 
                        : 'border-purple-500/30 text-purple-300 bg-purple-900/20'
                    }`}
                  >
                    {spin.wasFree ? (
                      'Grátis'
                    ) : (
                      <span className="flex items-center gap-1">
                        <Gem className="w-3 h-3" />
                        {spin.gemsSpent}
                      </span>
                    )}
                  </Badge>
                </div>
              ))}
            </div>
          </ScrollArea>
        )}
      </CardContent>
    </Card>
  );
};

export default WheelSpinHistory;
